import React, {useEffect, useState, useMemo} from "react";
import styled from "styled-components/macro";
import LoaderWithBackDrop from "../../../../components/LoaderWithBackDrop/LoaderWithBackDrop.comp";

import {
    Paper,
    Table,
    Box,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Grid as MuiGrid,
    Card as MuiCard,
    CardContent as MuiCardContent,
    Typography as MuiTypography,
    withStyles, Button as MuiButton,
} from "@material-ui/core";

import ColoredLinearProgress from "../../../PartnerDashboard/Assessment/components/ColouredLinearProgress.comp";

import { spacing } from "@material-ui/system";
import {useAuth} from "../../../../context/auth";
import XLSX from "xlsx";
import {alphaNumericSort, predefinedSort} from "../../../../utils/utils";
import {GenericSidePanel} from "../../../../components/SidePanel/GenericSidePanel.comp";
import {getUniqueModuleAssessL6CodesService, getModuleIndicatorTranslationsService, getOverallEfficacyService} from "../../../../services/school.service";
import {useLocation} from "react-router-dom";
import MonthRangePicker from "../../../../components/DatePickers/MonthRangePicker.comp";
import {GET_APP_LOCALIZE_TEXT} from "../../../../utils/dq_lc_service/lc_service";
import {useGlobalState} from "../../../../context/global_state";

const CustomTableCell = withStyles((theme) => ({
    head: {
        backgroundColor: '#233044',
        color: theme.palette.common.white,
    },
    body: {
        fontSize: 14,
    },
}))(TableCell);

const CustomTableRow = styled(TableRow)`
  &:nth-of-type(odd) {
    background-color: rgba(0, 0, 0, 0.025);
  }
`;

const Grid = styled(MuiGrid)(spacing);

const Card = styled(MuiCard)(spacing);

const Button = styled(MuiButton)(spacing);

const Typography = styled(MuiTypography)(spacing);

const CardContent = styled(MuiCardContent)`
  position: relative;
  padding-left: ${(props) => props.theme.spacing(12)}px;
  padding-right: ${(props) => props.theme.spacing(12)}px;

  &:first-child {
    padding-top: ${(props) => props.theme.spacing(12)}px;
  }
  &:last-child {
    padding-bottom: ${(props) => props.theme.spacing(12)}px;
  }
`;

const LinearProgress = styled(ColoredLinearProgress)`
  height: 14px;
  border-radius: 3px;
`

function LinearProgressWithLabel(props) {
    return (
        <Grid container spacing={4}>
            <Grid item xs={8}>
                    <LinearProgress variant="determinate" {...props} />
            </Grid>
            <Grid item xs={4}>
                <Typography variant="body2" color="textSecondary">{`${Math.round(props.value,)}%`}</Typography>
            </Grid>
        </Grid>
    );
}

const ColouredTypography = styled(Typography)`
  color: ${(props) => props.color ? props.color : props.theme.sidebar.background};
  font-size: ${(props) => props.size ? props.size : "default"};
  text-align: ${(props) => props.textAlign ? props.textAlign : "left"};
`

const Spacer = styled.div(spacing);

const DefaultColouredTypography = styled(Typography)`
  color: ${(props) => props.color ? props.color : props.theme.sidebar.background};
  font-size: ${(props) => props.size ? props.size : "default"};
  font-family: "Lemon/Milk";
`

const Bold = (props) => <Box component={'span'} fontWeight="fontWeightBold" {...props}></Box>

function OverallEfficacyTab({skill, description, header1, header2}) {

    const { authUser } = useAuth();
    const { org_code } = authUser;

    const location = useLocation();
    const pageLc = location.pathname.split('/')[1];
    const { settings } = useGlobalState();
    const module_code = settings?.selected_report;

    const competencyHeader = GET_APP_LOCALIZE_TEXT(pageLc, "efficacy__select_competency") || "Select a Competency"
    const SELECT_ALL_KEY = 'SELECT_ALL_INDICATORS';
    const MIN_USERS = 10;

    //global data
    const [pageData, setPageData] = useState(undefined);

    //indicators data
    const [modules, setModules] = useState([]);
    const [indicators, setIndicators] = useState([]);
    const [selectedIndicators, setSelectedIndicators] = useState([]);
    const [indicatorCheckboxState, setIndicatorCheckboxState] = useState({});
    const [sidePanelKey, setSidePanelKey] = useState(0);

    //time period data
    const [timePeriods, setTimePeriods] = useState([]);
    const [timePeriodsReset, setTimePeriodsReset] = useState(false);
    const [resetKey, setResetKey] = useState(false);
    const [timeCheckboxState, setTimeCheckboxState] = useState({});

    //selector states
    const [selectedTime, setSelectedTime] = useState([undefined, undefined]);

    //misc states
    const [minDate, setMinDate] = useState(undefined);
    const [maxDate, setMaxDate] = useState(undefined);
    const [errorMessage, setErrorMessage] = useState('');
    const [loading, setLoading] = useState(false);

    const secondarySideHeader = competencyHeader
    const timeSideHeader= GET_APP_LOCALIZE_TEXT(pageLc, "side_panel__select_time")
    const selectAllTimesOption = GET_APP_LOCALIZE_TEXT(pageLc, "side_panel__all_year")

    const allActiveIndicators = useMemo(() => {
        return modules.flatMap(module => 
            (module.indicators || []).filter(ind => !ind.disabled).map(ind => ind.indicator_code)
        );
    }, [modules]);

    useEffect(() => {
        if (module_code) {
            uniqueAssessL6Codes().then(r => console.log('Unique L6 Codes Fetched!', r));
        }
    }, [module_code]);

    // Initialize time periods with default range (current year and 4 years back)
    useEffect(() => {
        if (timePeriods.length === 0) {
            const currentYear = new Date().getFullYear();
            const years = [];
            for (let i = 0; i < 5; i++) {
                years.push((currentYear - i).toString());
            }
            setTimePeriods(years.reverse());
        }
    }, []);

    useEffect(() => {
        if (selectedIndicators.length > 0 && selectedTime[0] && selectedTime[1]) {
            loadPageData().then(d => console.log('Overall Efficacy Data Fetched', d));
        } else if (selectedIndicators.length === 0 || !selectedTime[0] || !selectedTime[1]) {
            setPageData(undefined);
        }
    }, [selectedIndicators, selectedTime])

    useEffect(() => {
        if (allActiveIndicators.length > 0) {
            const allSelected = allActiveIndicators.every(code => selectedIndicators.includes(code));
            setIndicatorCheckboxState(prevState => ({
                ...prevState,
                [SELECT_ALL_KEY]: allSelected
            }));
        }
    }, [selectedIndicators, allActiveIndicators])

    useEffect(() => {
        if(timePeriods.length > 0)
        {
            const [firstYear, lastYear] = [timePeriods[0], timePeriods[timePeriods.length - 1]]
            setMinDate(new Date(`${firstYear}-01-01`))
            setMaxDate(new Date(`${lastYear}-12-31`))
            selectAllMonthsOfYears(timePeriods);
            setTimePeriodsReset(true);
        }
    },[timePeriods])

    useEffect(() => {
        if (allActiveIndicators.length > 0 && selectedIndicators.length === 0) {
            setSelectedIndicators(allActiveIndicators);
            const newState = { [SELECT_ALL_KEY]: true };
            allActiveIndicators.forEach(code => {
                newState[code] = true;
            });
            setIndicatorCheckboxState(newState);
        }
    }, [allActiveIndicators])

    useEffect(() => {
        // Initialize time checkbox state with "All Year" selected
        setTimeCheckboxState({
            [selectAllTimesOption]: true,
        });
    }, [selectAllTimesOption])

    // Force remount when both states are ready (only once)
    const [initialStatesSet, setInitialStatesSet] = useState(false);
    useEffect(() => {
        const timeStateReady = Object.keys(timeCheckboxState).length > 0;
        const indicatorStateReady = Object.keys(indicatorCheckboxState).length > 0;

        if (timeStateReady && indicatorStateReady && !initialStatesSet) {
            setInitialStatesSet(true);
            setSidePanelKey(prev => prev + 1);
        }
    }, [timeCheckboxState, indicatorCheckboxState, initialStatesSet])


    const uniqueAssessL6Codes = async () => {
        if (!module_code) return;
        try {
            setLoading(true);
            const response = await getUniqueModuleAssessL6CodesService(org_code, module_code);
            if (response.data && response.data.modules) {
                // mark disabled for each indicator based on unique_users_count
                const modulesWithDisabled = (response.data.modules || []).map(module => ({
                    ...module,
                    indicators: (module.indicators || []).map(ind => ({
                        ...ind,
                        disabled: typeof ind.unique_users_count === 'number' ? (ind.unique_users_count < MIN_USERS) : false
                    }))
                }));

                // Extract all module codes and indicator codes
                const allModuleCodes = modulesWithDisabled.map(module => module.module_code);
                const allIndicatorCodes = modulesWithDisabled.flatMap(module => (module.indicators || []).map(ind => ind.indicator_code));

                // Fetch translations with default locale 'en'
                const translationsResponse = await getModuleIndicatorTranslationsService('en', allModuleCodes, allIndicatorCodes);

                if (translationsResponse && translationsResponse.data) {
                    const { modules: moduleTranslationsArray = [], indicators: indicatorTranslationsArray = [] } = translationsResponse.data;

                    // Convert arrays to lookup maps for easier access
                    const moduleTranslationsMap = Object.fromEntries(
                        moduleTranslationsArray.map(m => [m.module_code, m.module_name])
                    );
                    const indicatorTranslationsMap = Object.fromEntries(
                        indicatorTranslationsArray.map(i => [i.indicator_code, i.indicator_name])
                    );

                    // Merge translations into modules and indicators
                    const modulesWithTranslations = modulesWithDisabled.map(module => ({
                        ...module,
                        module_name: moduleTranslationsMap[module.module_code] || module.module_code,
                        indicators: (module.indicators || []).map(ind => ({
                            ...ind,
                            indicator_name: indicatorTranslationsMap[ind.indicator_code] || ind.indicator_code
                        }))
                    }));

                    const sortedModules = modulesWithTranslations.sort((a, b) => {
                        const aHasEnabled = (a.indicators || []).some(ind => !ind.disabled);
                        const bHasEnabled = (b.indicators || []).some(ind => !ind.disabled);
                        if (aHasEnabled && !bHasEnabled) return -1;
                        if (!aHasEnabled && bHasEnabled) return 1;
                        return 0;
                    });

                    setModules(sortedModules);
                    setIndicators(allIndicatorCodes);
                    // Initialize with all active indicators checked
                    const activeIndicatorCodes = sortedModules.flatMap(module => 
                        (module.indicators || []).filter(ind => !ind.disabled).map(ind => ind.indicator_code)
                    );
                    setIndicatorCheckboxState(Object.fromEntries([
                        [SELECT_ALL_KEY, true],
                        ...allIndicatorCodes.map(code => [code, activeIndicatorCodes.includes(code)])
                    ]));
                } else {
                    const sortedModules = modulesWithDisabled.sort((a, b) => {
                        const aHasEnabled = (a.indicators || []).some(ind => !ind.disabled);
                        const bHasEnabled = (b.indicators || []).some(ind => !ind.disabled);
                        if (aHasEnabled && !bHasEnabled) return -1;
                        if (!aHasEnabled && bHasEnabled) return 1;
                        return 0;
                    });

                    setModules(sortedModules);
                    setIndicators(allIndicatorCodes);
                    // Initialize with all active indicators checked
                    const activeIndicatorCodes = sortedModules.flatMap(module => 
                        (module.indicators || []).filter(ind => !ind.disabled).map(ind => ind.indicator_code)
                    );
                    setIndicatorCheckboxState(Object.fromEntries([
                        [SELECT_ALL_KEY, true],
                        ...allIndicatorCodes.map(code => [code, activeIndicatorCodes.includes(code)])
                    ]));
                }
            }

        } catch (e) {
            console.log("Error fetching unique L6 codes or translations:", e);
        } finally {
            setLoading(false);
        }
    }

    const transformApiData = (apiData) => {
        if (!apiData || !Array.isArray(apiData) || apiData.length === 0) {
            return [];
        }

        const transformedData = apiData
            .filter(item => item && item.assess6_code)
            .map(item => {
                let participants, skills, rate;

                if (skill === "Acquisition") {
                    participants = item.unique_users_count || 0;
                    skills = item.unique_users_count_rub_success || 0;
                    rate = (item.avg_assess6_rub !== undefined && item.avg_assess6_rub !== null)
                        ? item.avg_assess6_rub * 100
                        : (participants > 0 ? (skills / participants) * 100 : 0);
                } else if (skill === "Improvement") {
                    participants = item.unique_users_count || 0;
                    skills = item.unique_users_count_improved || 0;
                    rate = (item.avg_assess6_imp_rub !== undefined && item.avg_assess6_imp_rub !== null)
                        ? item.avg_assess6_imp_rub * 100
                        : (participants > 0 ? (skills / participants) * 100 : 0);
                } else {
                    participants = item.unique_users_count || 0;
                    skills = item.unique_users_count_changed || 0;
                    rate = (item.avg_assess6_ifc_rub !== undefined && item.avg_assess6_ifc_rub !== null)
                        ? item.avg_assess6_ifc_rub * 100
                        : (participants > 0 ? (skills / participants) * 100 : 0);
                }

                let displayName;
                try {
                    displayName = getIndicatorDisplayName(item.assess6_code) || item.assess6_code;
                } catch (e) {
                    displayName = item.assess6_code;
                }

                return {
                    topic: displayName,
                    assess6_code: item.assess6_code,
                    participants: participants,
                    skills: skills,
                    rate: rate
                };
            });

        const rowsWithoutTotal = transformedData.filter(item => item.topic !== "Total");
        const totalParticipants = rowsWithoutTotal.reduce((sum, item) => sum + item.participants, 0);
        const totalSkills = rowsWithoutTotal.reduce((sum, item) => sum + item.skills, 0);
        const totalRate = totalParticipants !== 0 ? (totalSkills / totalParticipants) * 100 : 0;

        transformedData.push({
            topic: "Total",
            assess6_code: "Total",
            participants: totalParticipants,
            skills: totalSkills,
            rate: totalRate
        });

        return transformedData;
    };

    const loadPageData = async (time = selectedTime) => {
        const startTime = time[0] instanceof Date ? time[0].toISOString() : time[0];
        const endTime = time[1] instanceof Date ? time[1].toISOString() : time[1];

        if (selectedIndicators.length === 0 || !startTime || !endTime || !module_code) {
            setPageData(undefined);
            return;
        }
        setLoading(true);
        try {
            setPageData(undefined);
            const result = await getOverallEfficacyService(
                org_code,
                module_code,
                selectedIndicators,
                startTime,
                endTime
            );
            if (result && result.data) {
                let rawData = [];
                
                if (Array.isArray(result.data)) {
                    rawData = result.data;
                } else if (result.data.data && Array.isArray(result.data.data)) {
                    rawData = result.data.data;
                } else if (typeof result.data === 'object' && result.data.assess6_code) {
                    rawData = [result.data];
                } else if (typeof result.data === 'object') {
                    const keys = Object.keys(result.data);
                    for (let key of keys) {
                        if (Array.isArray(result.data[key])) {
                            rawData = result.data[key];
                            break;
                        }
                    }
                    if (rawData.length === 0 && result.data.assess6_code) {
                        rawData = [result.data];
                    }
                }
                
                if (rawData.length === 0) {
                    setErrorMessage(GET_APP_LOCALIZE_TEXT(pageLc, "general__data_unavailable"));
                    setPageData(undefined);
                } else {
                    const transformedData = transformApiData(rawData);
                    
                    if (transformedData && transformedData.length > 0) {
                        setPageData({
                            data: transformedData
                        });
                        setErrorMessage('');
                    } else {
                        setErrorMessage(GET_APP_LOCALIZE_TEXT(pageLc, "general__data_unavailable"));
                        setPageData(undefined);
                    }
            }
            } else {
                setErrorMessage(GET_APP_LOCALIZE_TEXT(pageLc, "general__data_unavailable"));
            }
        } catch (e) {
            console.log("Some error occurred while fetching overall efficacy data", e);
            setErrorMessage(GET_APP_LOCALIZE_TEXT(pageLc, "general__data_unavailable"));
        }
        setTimeout(() => {setLoading(false)}, 200);
        return true;
    }

    function areDateRangesEqual(range1, range2)
    {
        if(!Array.isArray(range1) || !Array.isArray(range2)) return false;
        if(range1.length !== range2.length) return false;
        for(let index in range1)
        {
            if(range1[index] !== range2[index]) return false;
        }
        return true;
    }


    const flattenedData = modules.flatMap(module => {
        const moduleItem = `MODULE:${module.module_name || module.module_code}`;
        const moduleIndicators = (module.indicators || []).map(ind => ind.indicator_code);
        return [moduleItem, ...moduleIndicators];
    });

    const flattenedDataWithSelectAll = [SELECT_ALL_KEY, ...flattenedData];

    const disabledSecondaryItems = modules.flatMap(module => (module.indicators || []).filter(ind => ind.disabled).map(ind => ind.indicator_code));

    const getIndicatorDisplayName = (indicatorCode) => {
        for (const module of modules) {
            const indicator = (module.indicators || []).find(ind => ind.indicator_code === indicatorCode);
            if (indicator) {
                return indicator.indicator_name || indicatorCode;
            }
        }
        return indicatorCode;
    };

    function onSelectIndicator(item, isChecked, state, setState) {
        if (item === SELECT_ALL_KEY) {
            if (isChecked) {
                const newSelectedIndicators = [...allActiveIndicators];
                setSelectedIndicators(newSelectedIndicators);
                const newState = { ...state, [SELECT_ALL_KEY]: true };
                allActiveIndicators.forEach(code => {
                    newState[code] = true;
                });
                setState(newState);
                setIndicatorCheckboxState(newState);
            } else {
                setSelectedIndicators([]);
                const newState = { ...state, [SELECT_ALL_KEY]: false };
                allActiveIndicators.forEach(code => {
                    newState[code] = false;
                });
                setState(newState);
                setIndicatorCheckboxState(newState);
            }
            return;
        }

        const indicatorCode = item;
        const indicatorObj = modules
            .flatMap(m => m.indicators || [])
            .find(ind => ind.indicator_code === indicatorCode);

        if (indicatorObj?.disabled) return;

        if (isChecked) {
            setSelectedIndicators(prev => {
                const newIndicators = [...prev, indicatorCode];
                const allSelected = allActiveIndicators.length > 0 && allActiveIndicators.every(code =>
                    newIndicators.includes(code)
                );
                const newState = {
                    ...state,
                    [indicatorCode]: true,
                    [SELECT_ALL_KEY]: allSelected
                };
                setState(newState);
                setIndicatorCheckboxState(prevState => ({
                    ...prevState,
                    [indicatorCode]: true,
                    [SELECT_ALL_KEY]: allSelected
                }));
                return newIndicators;
            });
        } else {
            setSelectedIndicators(prev => {
                const newIndicators = prev.filter(code => code !== indicatorCode);
                const newState = {
                    ...state,
                    [indicatorCode]: false,
                    [SELECT_ALL_KEY]: false
                };
                setState(newState);
                setIndicatorCheckboxState(prevState => ({
                    ...prevState,
                    [indicatorCode]: false,
                    [SELECT_ALL_KEY]: false
                }));
                return newIndicators;
            });
        }
    }

    function onSelectTimePeriod (item, isChecked, state, setState) {
        if(isChecked)
        {
            if(item === selectAllTimesOption)
            {
                selectAllMonthsOfYears(timePeriods);
            }
            else
            {
                selectAllMonthsOfYears([item]);
            }
            const nextState = Object.fromEntries([selectAllTimesOption, ...timePeriods].map(
                (time) => time === item ? [time, true] : [time, false]
            ));
            setState(nextState);
            setTimeCheckboxState(nextState);
            setResetKey(prevReset => !prevReset);
        }
        else
        {
            console.log("Oops")
            setState({
                ...state,
                [item]: true,
            });
        }
    }

    function onClickDatePicker(event, state, setState) {
        setState({
            [selectAllTimesOption]: false,
        });
    }

    function onSelectDateRange(dates) {

        const start_date = new Date(Date.parse(dates[0])).toISOString();
        const end_date = new Date(Date.parse(dates[1])).toISOString();

        console.log(start_date, end_date);

        setSelectedTime([start_date, end_date]);
    }

    const selectAllMonthsOfYears = (years) => {
        if (!years || years.length === 0) return;

        const [startYear, endYear] = [years[0], years[years.length-1]];
        const [startDate, endDate] = [new Date(`${startYear}-01-01`).toISOString(), new Date(`${endYear}-12-31`).toISOString()]
        setSelectedTime([startDate, endDate]);
    }

    const onClickDownload = (event) => {
        if (!pageData || !Array.isArray(pageData.data) || pageData.data.length === 0) {
            return;
        }

        const workbook = XLSX.utils.book_new();
        let column_widths = []

        const final_array = [
            [`${GET_APP_LOCALIZE_TEXT(pageLc, "side_panel__selected_time")}:`, new Date(selectedTime[0]).toDateString(), new Date(selectedTime[1]).toDateString()],
            [" "],
            [GET_APP_LOCALIZE_TEXT(pageLc, "efficacy__modules") || "Modules", GET_APP_LOCALIZE_TEXT(pageLc, "efficacy__participants") || "Participants", header1, header2]
        ]

        const items_array = pageData.data.sort(alphaNumericSort("topic", true)).sort(predefinedSort(["Total"], "topic", true)).map((item) => {
            return [item.topic, item.participants.toString(), item.skills.toString(), `${Math.round(item.rate)}%`]
        })

        final_array.push(...items_array);

        final_array.forEach((array) => {
            array.forEach(((item, index) => column_widths[index] =  column_widths[index] ? Math.max(item.toString().length, column_widths[index], 10) : item.length))
        })

        const worksheet = XLSX.utils.aoa_to_sheet(final_array);
        worksheet["!cols"] = new Array(column_widths.length).fill(null).map((_, index) => {
            return { wch: column_widths[index] }
        });
        XLSX.utils.book_append_sheet(workbook, worksheet, skill);
        XLSX.writeFile(workbook, `Assessment Efficacy ${skill}.xlsx`);
    }

    const handleTimePeriodsResetAcknowledged = () => {
        setTimePeriodsReset(false);
    };

    return (
        <Paper container>
            <Grid container>
                <Grid item xs={12} sm={12} md={3}>
                    <GenericSidePanel
                        key={sidePanelKey}
                        data={[
                            [selectAllTimesOption],
                            flattenedDataWithSelectAll,
                        ]}
                        headers={[
                            timeSideHeader,
                            secondarySideHeader,
                        ]}
                        visible={[true, true]}
                        colourize={[false, false]}
                        defaultCheckIndices={[0, -1]}
                        singleSelectIndices={[0, undefined]}
                        initialCheckboxStates={[
                            timeCheckboxState,
                            indicatorCheckboxState,
                        ]}
                        onCheckCallbacks={[
                            onSelectTimePeriod,
                            onSelectIndicator,
                        ]}
                        footers={[
                            MonthRangePicker,
                            undefined,
                        ]}
                        footersData={[
                            {onChangeDate: onSelectDateRange, minDate, maxDate, resetKey},
                            undefined,
                        ]}
                        footersCallbacks={[
                            {onOpen: onClickDatePicker},
                            undefined,
                        ]}
                        timePeriodsReset={timePeriodsReset}
                        onResetAcknowledged={handleTimePeriodsResetAcknowledged}
                        disabledItems={[
                            [],
                            disabledSecondaryItems,
                        ]}
                        labelFormatters={[
                            undefined,
                            (item) => {
                                if (item === SELECT_ALL_KEY) {
                                    return GET_APP_LOCALIZE_TEXT(pageLc, "side_panel__select_all", "Select All");
                                }
                                return getIndicatorDisplayName(item);
                            },
                        ]}
                    />
                </Grid>
                <Grid item xs={12} sm={12} md={9} alignItems={!pageData ? 'center' : ''} container={!pageData} justify={'center'}>
                    {
                        pageData && Array.isArray(pageData.data) && pageData.data.length > 0 ?
                            <Card mb={3}>
                                <CardContent>
                                    <ColouredTypography variant={"body1"} size={"16px"} color={"dimgrey"} align={"center"} alignText={"justify"} mb={10}>
                                        {description}
                                    </ColouredTypography>
                                    <Table>
                                        <TableHead>
                                            <TableRow>
                                                <CustomTableCell align="center">
                                                    <DefaultColouredTypography variant={"h6"} color={"white"}>
                                                        {GET_APP_LOCALIZE_TEXT(pageLc, "efficacy__competencies")}
                                                    </DefaultColouredTypography>
                                                </CustomTableCell>
                                                <CustomTableCell align="center">
                                                    <DefaultColouredTypography variant={"h6"} color={"white"}>
                                                        {GET_APP_LOCALIZE_TEXT(pageLc, "assessment__participants")}
                                                    </DefaultColouredTypography>
                                                </CustomTableCell>
                                                <CustomTableCell align="center">
                                                    <DefaultColouredTypography variant={"h6"} color={"white"}>
                                                        {header1}
                                                    </DefaultColouredTypography>
                                                </CustomTableCell>
                                                <CustomTableCell align="center">
                                                    <DefaultColouredTypography variant={"h6"} color={"white"}>
                                                        {header2}
                                                    </DefaultColouredTypography>
                                                </CustomTableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {
                                        pageData && Array.isArray(pageData.data) && pageData.data.length > 0
                                            ? pageData.data
                                                    .slice()
                                                    .sort(alphaNumericSort("topic", true))
                                                    .sort(predefinedSort(["Total"], "topic", true))
                                                    .map((item, index) => (
                                                        <CustomTableRow key={index}>
                                                            {
                                                                item.topic === "Total" ?
                                                                    <>
                                                                        <CustomTableCell align="center">
                                                                            <Bold>{GET_APP_LOCALIZE_TEXT(pageLc, "general__total")}</Bold>
                                                                        </CustomTableCell>
                                                                        <CustomTableCell align="center">
                                                                            <Bold>{item.participants}</Bold>
                                                                        </CustomTableCell>
                                                                        <CustomTableCell align="center">
                                                                            <Bold>{item.skills}</Bold>
                                                                        </CustomTableCell>
                                                                    </>
                                                                    :
                                                                    <>
                                                                        <CustomTableCell align="center">{item.topic}</CustomTableCell>
                                                                        <CustomTableCell align="center">{item.participants}</CustomTableCell>
                                                                        <CustomTableCell align="center">{item.skills}</CustomTableCell>
                                                                    </>

                                                            }
                                                            <CustomTableCell align="center">
                                                                <LinearProgressWithLabel value={item.participants !== 0 ? Math.round(item.skills/item.participants*100) : 0}>
                                                                </LinearProgressWithLabel>
                                                            </CustomTableCell>
                                                        </CustomTableRow>
                                                    ))
                                            : null
                                            }
                                        </TableBody>
                                    </Table>
                                    <Box textAlign={'right'}>
                                        <Button mt={5} variant="contained" onClick={onClickDownload}>
                                            {GET_APP_LOCALIZE_TEXT(pageLc, "general__download_data")}
                                        </Button>
                                    </Box>
                                </CardContent>
                            </Card>
                            :
                            <DefaultColouredTypography variant={"h3"} align={'center'}>{errorMessage}</DefaultColouredTypography>
                    }
                </Grid>
            </Grid>
            <LoaderWithBackDrop loading={loading} />
        </Paper>
    );
}

export default OverallEfficacyTab;